import * as React from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { FaIcon, faClose } from '@/lib/fa'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useMutationUpdateDatabaseLabel } from '@/lib/api/company/useCompanyDatabaseHooks'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

interface UpdateLabelModalProps {
  isOpen: boolean
  close: () => void
  companyId: string
  databaseId: number
  currentLabel: string
}

const UpdateLabelModal: React.FC<UpdateLabelModalProps> = ({
  isOpen,
  close,
  companyId,
  databaseId,
  currentLabel,
}) => {
  const loading = useLoadingStore(selectLoading)
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { t } = useLocalize()
  const mutation = useMutationUpdateDatabaseLabel(companyId)
  const [label, setLabel] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (isOpen) {
      setLabel(currentLabel || '')
      setError(null)
    }
  }, [isOpen, currentLabel])

  const handleConfirm = async () => {
    setError(null)
    const stopLoading = startLoading()
    try {
      await mutation.mutateAsync({ companyId, databaseId, label })
      close()
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        t('databases.update-label-error')
      setError(message)
    } finally {
      stopLoading()
    }
  }

  if (!isOpen) return null

  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[500px] tw-flex-col tw-rounded-lg tw-bg-white">
        {loading && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-select-none tw-place-items-center tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}

        {/* Header */}
        <div className="tw-border-b tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-items-center tw-justify-between">
            <p className="tw-text-xl tw-font-semibold tw-text-content-base">
              {t('databases.change-label')}
            </p>
            <FaIcon
              icon={faClose}
              className="tw-h-5 tw-w-5 tw-cursor-pointer"
              onClick={close}
            />
          </div>
        </div>

        {/* Content */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-6">
          <div className="tw-space-y-4">
            <div>
              <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-content-base">
                {t('administrationPanel-companies.database-label')}
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="tw-w-full tw-rounded-lg tw-border tw-border-base-300 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base focus:tw-border-primary focus:tw-outline-none"
                placeholder={t('databases.label-placeholder')}
                autoFocus
              />
            </div>

            {error && (
              <div className="tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-p-4">
                <p className="tw-text-sm tw-text-red-800">{error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="tw-border-t tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-justify-end tw-gap-3">
            <button
              type="button"
              onClick={close}
              disabled={loading}
              className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-100 disabled:tw-opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </ModalLayout>
  )
}

export default UpdateLabelModal
